//@flow
import React from 'react';
import {
  CircularProgress,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import {Check, Close, EditOutlined} from '@material-ui/icons';

import {colors} from '../../../../assets/styles/colors';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useConversationNotes} from '../utils';

type Props = {
  conversationId: string | number,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    padding: theme.spacing(0, 1, 1),
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '.5rem',
  },
  content: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    flex: 1,
  },
  emptyText: {
    color: colors.grey500,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    flex: 1,
  },
  editButton: {
    padding: '.25rem',
    color: colors.grey700,
  },
  textarea: {
    width: '100%',
    minHeight: 96,
    padding: '.5rem',
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,
    outline: 'none',
    fontSize: '.875rem',
    color: colors.grey900,
    fontFamily: 'inherit',
    resize: 'vertical',
    lineHeight: 1.5,
    '&::placeholder': {
      color: colors.grey500,
    },
    '&:focus': {
      borderColor: colors.primary,
    },
  },
  counter: {
    color: colors.grey500,
    fontSize: 12,
    textAlign: 'right',
  },
  counterLimit: {
    color: colors.danger,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '.5rem',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 0),
  },
  error: {
    color: colors.danger,
    fontSize: 12,
  },
}));

export default function ConversationNotesSection(props: Props) {
  const {conversationId} = props;
  const classes = useStyles();

  const {
    content,
    draft,
    isEditing,
    loading,
    saving,
    error,
    startEditing,
    cancelEditing,
    updateDraft,
    saveNote,
    maxCharacters,
  } = useConversationNotes(conversationId);

  const isAtLimit = draft.length >= maxCharacters;

  if (loading) {
    return (
      <div className={classes.loader}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={classes.root}>
        <textarea
          className={classes.textarea}
          value={draft}
          maxLength={maxCharacters}
          placeholder="Escribe una nota"
          onChange={event => updateDraft(event.target.value)}
          disabled={saving}
        />
        <Typography
          className={`${classes.counter} ${
            isAtLimit ? classes.counterLimit : ''
          }`}
        >
          {draft.length}/{maxCharacters} caracteres
        </Typography>
        {error && <Typography className={classes.error}>{error}</Typography>}
        <div className={classes.actions}>
          <CustomButton
            variant="text"
            color="default"
            onClick={cancelEditing}
            disabled={saving}
          >
            <Close fontSize="small" style={{marginRight: 4}} />
            Cancelar
          </CustomButton>
          <CustomButton
            variant="text"
            color="primary"
            onClick={saveNote}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={18} />
            ) : (
              <>
                <Check fontSize="small" style={{marginRight: 4}} />
                Guardar
              </>
            )}
          </CustomButton>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {content ? (
          <Typography className={classes.content}>{content}</Typography>
        ) : (
          <Typography className={classes.emptyText}>
            Sin notas. Haz clic en el lápiz para agregar una.
          </Typography>
        )}
        <IconButton
          size="small"
          className={classes.editButton}
          onClick={startEditing}
          aria-label="Editar nota"
        >
          <EditOutlined fontSize="small" />
        </IconButton>
      </div>
      {error && <Typography className={classes.error}>{error}</Typography>}
    </div>
  );
}
